<template>
  <SFButton
    :aria-busy="mounted && status === 'pending'"
    :aria-disabled="isWishlistToggling"
    :aria-label="
      isInWishlist
        ? $t('wishlist_toggle.remove_from_wishlist')
        : $t('wishlist_toggle.add_to_wishlist')
    "
    :data-testid="
      isInWishlist
        ? 'remove-item-from-wishlist-button'
        : 'add-item-to-wishlist-button'
    "
    class="rounded-md border-none bg-transparent pl-0.5"
    size="md"
    v-bind="$attrs"
    variant="raw"
    @click="onToggleWishlist"
  >
    <template #icon>
      <SFAsyncStatusWrapper :status="wishlistStatus">
        <IconHeartInactive
          v-if="
            (!isInWishlist && !isWishlistToggling) ||
            (isInWishlist && isWishlistToggling)
          "
          class="size-6"
        />
        <IconHeartActive v-else class="size-6" />
        <template #loading> <IconHeartInactive class="size-6" /> </template>
      </SFAsyncStatusWrapper>
    </template>
    <span v-if="hasLabel" class="text-sm">{{ wishlistLabelText }}</span>
  </SFButton>
</template>

<script lang="ts" setup>
import { computed, ref, toValue } from 'vue'
import type { Product, Variant } from '@scayle/storefront-nuxt'
import { useMounted } from '@vueuse/core'
import SFAsyncStatusWrapper from '../SFAsyncStatusWrapper.vue'
import { useToast, useWishlistTracking } from '~/composables'
import { useWishlist } from '#storefront/composables'
import { SFButton } from '#storefront-ui/components'
import type { ListItem, WishlistLabelTypes } from '~/types/tracking'
import { useI18n } from '#i18n'
import { useRoute } from '#imports'

const {
  product,
  listingMetaData,
  hasLabel = false,
  variant,
  isCarouselItem = false,
} = defineProps<{
  product: Product
  listingMetaData?: ListItem
  hasLabel?: boolean
  variant?: Variant
  isCarouselItem?: boolean
}>()

defineOptions({ inheritAttrs: false })

const allowedLabels = [
  'category',
  'product detail page',
  'cart page',
  'search result page',
  'product carousel',
  'wishlist page',
] as const satisfies readonly WishlistLabelTypes[]

const route = useRoute()
const mounted = useMounted()
const { t } = useI18n()
const isWishlistToggling = ref(false)
const productId = computed(() => product.id)
const toast = useToast()
const { toggleItem, contains, status } = useWishlist()

const { trackWishlistItemEvent } = useWishlistTracking()

const onToggleWishlist = async () => {
  if (isWishlistToggling.value) {
    return
  }

  const wasInWishlist = contains({ productId: productId.value })

  trackWishlistItemEvent(!wasInWishlist ? 'added' : 'removed', {
    product: toValue(product),
    ...(toValue(listingMetaData) && {
      listingMetaData: toValue(listingMetaData),
    }),
    ...(toValue(variant) && { variant: toValue(variant) }),
    label: wishlistTrackLabel(),
  })

  isWishlistToggling.value = true
  await toggleItem({
    productId: productId.value,
    variantId: toValue(variant)?.id,
  })
  if (status.value === 'success') {
    const wishlistMessage = isInWishlist.value
      ? t('wishlist_toggle.notification.add_product_to_wishlist')
      : t('wishlist_toggle.notification.remove_product_from_wishlist')
    toast.show(wishlistMessage, {
      type: 'SUCCESS',
      duration: 2000,
      withProgress: true,
    })
  }

  isWishlistToggling.value = false
}

const isInWishlist = computed(() => {
  return contains({ productId: productId.value })
})
const wishlistLabelText = computed(() =>
  isInWishlist.value
    ? t('wishlist_toggle.remove_from_wishlist')
    : t('wishlist_toggle.add_to_wishlist'),
)
const wishlistStatus = computed(() => {
  return mounted.value ? status.value : 'pending'
})

function wishlistTrackLabel(): WishlistLabelTypes {
  if (isCarouselItem) {
    return 'product carousel'
  }

  const routeMeta = (route.meta.pageType as string).split('_').join(' ')

  if (allowedLabels.includes(routeMeta as WishlistLabelTypes)) {
    // assert here so TS knows it's safe
    return routeMeta as WishlistLabelTypes
  } else {
    return 'category'
  }
}
</script>
